import React from 'react'
import {
  Box,
  Checkbox,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import {
  CSVTask,
  csvTaskLabels,
  csvTasks,
  csvUploadText,
} from './CSVUpload.config'
import { CSVUploadDialogActions } from './CSVUploadDialogActions'
import type { CSVUploadDialogProps } from './CSVUpload.types'

/**
 * Dialog UI for CSV task selection, template preview, and upload actions.
 * @param props - Dialog state and interaction handlers
 * @returns CSV upload dialog component
 */
export function CSVUploadDialog({
  open,
  onClose,
  selectedTask,
  onTaskChange,
  addRoles,
  onAddRolesChange,
  tableHeaders,
  fileName,
  errorMessage,
  summary,
  isLoadingColumns,
  isUploading,
  disabledState,
  uploadDisabledState,
  inputId,
  onFileChange,
  onDownloadTemplate,
}: CSVUploadDialogProps) {
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>{csvUploadText.dialogTitle}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <FormControl size="small" sx={{ maxWidth: 280 }}>
            <InputLabel id="csv-task-label">
              {csvUploadText.taskLabel}
            </InputLabel>
            <Select
              labelId="csv-task-label"
              label={csvUploadText.taskLabel}
              value={selectedTask}
              displayEmpty
              renderValue={(value) => {
                const taskValue = String(value)
                return taskValue.length > 0
                  ? csvTaskLabels[taskValue as CSVTask]
                  : csvUploadText.taskPlaceholder
              }}
              onChange={(event) =>
                onTaskChange(event.target.value as CSVTask | '')
              }
              data-testid="csv-task-select"
              disabled={disabledState}
            >
              <MenuItem disabled value="">
                {csvUploadText.taskPlaceholder}
              </MenuItem>
              {csvTasks.map((task) => (
                <MenuItem key={task} value={task}>
                  {csvTaskLabels[task]}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {selectedTask === 'updateUser' && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={addRoles}
                  onChange={(event) => onAddRolesChange(event.target.checked)}
                  size="small"
                  data-testid="csv-task-add-roles-checkbox"
                  disabled={disabledState}
                />
              }
              label={csvUploadText.addRolesLabel}
            />
          )}
          <TableContainer>
            <Table size="small" aria-label="CSV columns table">
              <TableHead>
                <TableRow>
                  {tableHeaders.length > 0 ? (
                    tableHeaders.map(({ name, required }, index) => (
                      <TableCell
                        key={`${required ? 'required' : 'optional'}-${name}-${index}`}
                        style={{
                          fontWeight: required ? 700 : 400,
                          fontStyle: required ? 'normal' : 'italic',
                        }}
                      >
                        {name}
                      </TableCell>
                    ))
                  ) : (
                    <TableCell>
                      {selectedTask !== ''
                        ? csvUploadText.columnsUnavailable
                        : csvUploadText.selectTaskToViewColumns}
                    </TableCell>
                  )}
                </TableRow>
              </TableHead>
            </Table>
          </TableContainer>
          <Typography
            variant="caption"
            color="text.secondary"
            data-testid="csv-task-column-style-legend"
          >
            Column styles: <b>bold</b> = required, <i>italic</i> = optional.
          </Typography>
          {fileName.length > 0 && (
            <Typography variant="body2" color="text.secondary">
              {fileName}
            </Typography>
          )}
          {errorMessage ? (
            <Typography variant="body2" color="error">
              {errorMessage}
            </Typography>
          ) : (
            <Typography variant="body2" color="text.secondary">
              {csvUploadText.helperText}
            </Typography>
          )}
          {summary && (
            <Typography variant="body2" color="text.secondary">
              Rows processed: {summary.rowsProcessed} / {summary.rowsReceived}
            </Typography>
          )}
        </Box>
      </DialogContent>
      <CSVUploadDialogActions
        disabledState={disabledState}
        uploadDisabledState={uploadDisabledState}
        isLoadingColumns={isLoadingColumns}
        isUploading={isUploading}
        inputId={inputId}
        tableHeaders={tableHeaders}
        onFileChange={onFileChange}
        onDownloadTemplate={onDownloadTemplate}
        onClose={onClose}
      />
    </Dialog>
  )
}
